import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import routes from '@/router';
import { IRoute } from "@/layouts/main/types";
import { ILayout, selectDisplayLayout, setDisplayLayout } from "@/store/slices/layoutSlice";
import { compareTwoObject } from "@/utils";
import { useShallowEqualSelector } from "@/hooks/useShallowEqualSelector";
import Auth from "@/layouts/Auth";
import { styled } from "@mui/material/styles";
import { GREY } from "@/theme/palette";
import { selectCurrentUser } from "@/store/slices/authSlice";

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;
const DRAWER_WIDTH = 240;

const RootStyle = styled("div", {
    shouldForwardProp: (prop) => prop !== "sidebar" && prop !== "open"
})<{ sidebar?: boolean; open?: boolean }>(({ theme, sidebar, open }) => ({
    display: 'flex',
    minHeight: '100%',
    overflow: 'hidden',
    width: '100%',
    backgroundColor: GREY["1000"],
    ...((open && sidebar) && {
        [theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${DRAWER_WIDTH}px)`
        }
    }),
    ...((!open && sidebar) && {
        [theme.breakpoints.up('lg')]: {
            width: '100%'
        }
    }),
}));

const MainStyle = styled('div', {
    shouldForwardProp: (prop) => prop !== "layout"
})<{ layout?: ILayout; }>(({ theme, layout }) => ({
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: (!layout.header && !layout.footer && !layout.sidebar && !layout.minimal) ? 0 : APP_BAR_MOBILE + 24,
    paddingBottom: (!layout.header && !layout.footer && !layout.sidebar && !layout.minimal) ? 0 : theme.spacing(10),
    [theme.breakpoints.up('lg')]: {
        paddingTop: (!layout.header && !layout.footer && !layout.sidebar && !layout.minimal) ? 0 : APP_BAR_DESKTOP + 1,
    }
}));


const Main: React.FC = () => {
    const layout: ILayout = useShallowEqualSelector(selectDisplayLayout);

    // store dispatch to render layouts
    const dispatch = useDispatch();

    const updateDisplayLayout = (currentLayout: ILayout, layout: ILayout) => {
        const layoutUpdated = currentLayout
            ? { header: !!currentLayout.header, footer: !!currentLayout.footer, sidebar: !!currentLayout.sidebar, minimal: !!currentLayout.minimal }
            : { header: true, footer: true, sidebar: true, minimal: false };

        if (!compareTwoObject(layoutUpdated, layout)) {
            setTimeout(() => dispatch(setDisplayLayout(layoutUpdated)));
        }
    };

    // obtener user scopes
    const user: any = useSelector(selectCurrentUser);

    return (
        <RootStyle sidebar={layout.sidebar}>
            <MainStyle layout={layout}>
                <Switch>
                    {
                        routes.map(({ component: Component, path, layout: currentLayout, isPublic, routeScopes, ...rest }: IRoute) => {
                            return (
                                <Route
                                    key={path}
                                    path={path}
                                    render={(props) => {
                                        // update layout by configs
                                        updateDisplayLayout(currentLayout, layout);

                                        // check if can render route
                                        return (
                                            <Auth
                                                isPublic={isPublic}
                                                userScopes={user?.scopes}
                                                userRoles={user?.roles}
                                                routeScopes={routeScopes}
                                            >
                                                <Component {...props} />
                                            </Auth>
                                        );
                                    }}
                                    {...rest}
                                />
                            );
                        })
                    }
                </Switch>
            </MainStyle>
        </RootStyle>
    );
};

export default Main;
